import { StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle } from 'lucide-react';
import { useIntegrations } from '../context';
import {
  IntegrationsHero,
  IntegrationsTabStrip,
  NeedsAttentionBand,
  NeedsAttentionBandSkeleton,
  AlreadyIntegratedTab,
  MarketplaceTab,
} from '../components';
import { useIntegrationsPermissions } from '../hooks';
import { INTEGRATIONS_TABS } from '../constants';

/**
 * Integrations home (CT-4443 Foundation + CT-4157 Hero + CT-4158 Tab Strip).
 *
 * Layout matches the prototype: Hero (full-bleed) → Needs-Your-Attention band
 * (CT-4159, admin-gated) → Tab Strip → active tab content. The band sits ABOVE
 * the strip; the strip is sticky so it stays in view while the tab content
 * scrolls. The Already Integrated tab renders the connected catalog (CT-4160
 * category groups + CT-4162 in-place search); Marketplace is a CT-3893 seam.
 * Only the content subtree is keyed on `activeTab`, so switching tabs never
 * re-mounts the hero, band, or strip.
 *
 * On a stats fetch error the hero is replaced by a failure message — the hook
 * resets stats to zeros, which would otherwise read as an empty workspace.
 * StatusMessage requires an `icon` (it renders it unconditionally) and reads
 * `subtitle`, not `description`.
 */
function IntegrationsHomePage() {
  const { t } = useTranslation('admin-studio');
  const { statsError, attentionItems, attentionLoading, attentionError, activeTab } =
    useIntegrations();
  const { canManageIntegrations } = useIntegrationsPermissions();

  // Needs-Your-Attention region (CT-4159): admin-gated. While the band's own gate
  // keeps it out of the DOM, this also gates the loading/error placeholders so a
  // non-admin sees nothing at all for the region.
  const showAttentionRegion =
    canManageIntegrations && (attentionLoading || attentionError || attentionItems.length > 0);

  const renderAttentionRegion = () => {
    if (attentionLoading) return <NeedsAttentionBandSkeleton />;
    if (attentionError) {
      return (
        <StatusMessage
          icon={AlertTriangle}
          title={t('common.requestFailed')}
          subtitle={attentionError}
        />
      );
    }
    return <NeedsAttentionBand items={attentionItems} />;
  };

  if (statsError) {
    return (
      <main className="flex-1 overflow-y-auto bg-surface-base scrollbar-aimsos-auto-sm">
        <StatusMessage
          icon={AlertTriangle}
          title={t('common.requestFailed')}
          subtitle={statsError}
        />
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto bg-surface-base scrollbar-aimsos-auto-sm">
      <IntegrationsHero />

      <div className="px-[32px] py-[24px]">
        {showAttentionRegion && <div className="mb-[24px]">{renderAttentionRegion()}</div>}

        {/* Sticky tab strip (prototype `.home-tabs`): bleeds to the gutter edges so
            its blurred background spans full width while content keeps the inset. */}
        <div className="sticky top-0 z-10 -mx-[32px] mb-[24px] border-b border-edge bg-surface-base/85 px-[32px] py-[14px] backdrop-blur">
          <IntegrationsTabStrip />
        </div>

        {activeTab === INTEGRATIONS_TABS.ALREADY_INTEGRATED ? (
          <div key="already-integrated">
            <AlreadyIntegratedTab />
          </div>
        ) : (
          <div key="marketplace">
            <MarketplaceTab />
          </div>
        )}
      </div>
    </main>
  );
}

export default IntegrationsHomePage;
